/**
 * Data access for the catalogue — categories, offerings, variants, the
 * comparison matrix, quote options, cities and the discount ladders. Plain
 * SELECT/UPDATE/INSERT wrapped in a `Result` so a failure is a value
 * `@repo/core` handles, never a thrown exception mid-use-case.
 *
 * **One file where there were two** (`offer.ts` + `production.ts`), because
 * there is one catalogue. A camera, a room, an operator and a montage package
 * are read and written by the same functions; what makes them behave
 * differently is three columns on the row, not three code paths.
 *
 * These functions are UNGUARDED on purpose: authorization is a `@repo/core`
 * concern (`assertPermission` runs there before any of these is called). Public
 * catalogue reads (the storefront) may call the `list*` readers directly; the
 * panel goes through the guarded `@repo/core` use-cases that wrap them.
 *
 * "Soft delete" in this schema is `isActive = false` (there is no `deletedAt`
 * column); the `update*` functions carry it.
 */
import { and, asc, count, eq, gte, inArray } from 'drizzle-orm'

import type { Database } from '../client.ts'
import { err, ok, type Result } from '../result.ts'
import { booking } from '../schema/bookings.ts'
import { capacityDay } from '../schema/capacity.ts'
import { cartItem } from '../schema/carts.ts'
import { city, offeringCity } from '../schema/geo.ts'
import {
  offering,
  offeringAvailability,
  offeringCategory,
  offeringFeature,
  offeringQuoteOption,
  offeringVariant,
  offeringVariantFeature,
} from '../schema/offerings.ts'
import { orderItem } from '../schema/orders.ts'
import { userSkill } from '../schema/skills.ts'
import { type DbError, dbError } from './errors.ts'

type CategoryRow = typeof offeringCategory.$inferSelect
type OfferingRow = typeof offering.$inferSelect
type VariantRow = typeof offeringVariant.$inferSelect
type FeatureRow = typeof offeringFeature.$inferSelect
type VariantFeatureRow = typeof offeringVariantFeature.$inferSelect
type QuoteOptionRow = typeof offeringQuoteOption.$inferSelect
type CityRow = typeof city.$inferSelect
type OfferingCityRow = typeof offeringCity.$inferSelect

// ── reads ──────────────────────────────────────────────────────────────────
//
// Every list is fetched whole rather than filtered per page. The catalogue is
// small (tens of rows), and a page showing three offerings would otherwise pay
// for three round-trips to answer one question. Filtering happens in memory,
// where it costs nothing and cannot produce a partially-loaded matrix.

export async function listOfferingCategories(
  db: Database,
): Promise<Result<CategoryRow[], DbError>> {
  try {
    return ok(await db.select().from(offeringCategory).orderBy(asc(offeringCategory.sortOrder)))
  } catch (cause) {
    return err(dbError('Failed to list offering categories', cause))
  }
}

export async function listOfferings(db: Database): Promise<Result<OfferingRow[], DbError>> {
  try {
    return ok(
      await db.select().from(offering).orderBy(asc(offering.categoryId), asc(offering.sortOrder)),
    )
  } catch (cause) {
    return err(dbError('Failed to list offerings', cause))
  }
}

export async function listOfferingVariants(db: Database): Promise<Result<VariantRow[], DbError>> {
  try {
    return ok(
      await db
        .select()
        .from(offeringVariant)
        .orderBy(asc(offeringVariant.offeringId), asc(offeringVariant.sortOrder)),
    )
  } catch (cause) {
    return err(dbError('Failed to list offering variants', cause))
  }
}

/**
 * Every level of ONE offering, in the order the price table lists them.
 *
 * Beside {@link listOfferingVariants} rather than instead of it: that one feeds
 * the whole catalogue read, this one answers a question asked about a single
 * paid line — "what could this be upgraded to, and for how much" (spec §8 · G9).
 * Loading the entire variant table to price one upgrade would be a read of
 * every level in the shop to compare two of them.
 *
 * Active rows only. A discontinued level is not something anybody may be sold
 * into, even though a line already sitting on one still reads back fine.
 */
export async function listVariantsOfOffering(
  db: Database,
  offeringId: string,
): Promise<Result<VariantRow[], DbError>> {
  try {
    return ok(
      await db
        .select()
        .from(offeringVariant)
        .where(and(eq(offeringVariant.offeringId, offeringId), eq(offeringVariant.isActive, true)))
        .orderBy(asc(offeringVariant.sortOrder)),
    )
  } catch (cause) {
    return err(dbError('Failed to list variants of an offering', cause))
  }
}

/** Every comparison row of every offering, ordered as the tables render them. */
export async function listOfferingFeatures(db: Database): Promise<Result<FeatureRow[], DbError>> {
  try {
    return ok(
      await db
        .select()
        .from(offeringFeature)
        .orderBy(asc(offeringFeature.offeringId), asc(offeringFeature.sortOrder)),
    )
  } catch (cause) {
    return err(dbError('Failed to list offering features', cause))
  }
}

/** The matrix cells: which variant grants which feature, and with what qualifier. */
export async function listOfferingVariantFeatures(
  db: Database,
): Promise<Result<VariantFeatureRow[], DbError>> {
  try {
    return ok(await db.select().from(offeringVariantFeature))
  } catch (cause) {
    return err(dbError('Failed to list offering variant features', cause))
  }
}

/**
 * The price-free things a client can ask to be quoted — the utility graphics
 * (wizytówka, ulotka, logo …) today. Deliberately no price column: scope varies
 * too much between a business card and a full ad set.
 */
export async function listOfferingQuoteOptions(
  db: Database,
): Promise<Result<QuoteOptionRow[], DbError>> {
  try {
    return ok(
      await db
        .select()
        .from(offeringQuoteOption)
        .orderBy(asc(offeringQuoteOption.offeringId), asc(offeringQuoteOption.sortOrder)),
    )
  } catch (cause) {
    return err(dbError('Failed to list offering quote options', cause))
  }
}

export async function listCities(db: Database): Promise<Result<CityRow[], DbError>> {
  try {
    return ok(await db.select().from(city).orderBy(asc(city.sortOrder)))
  } catch (cause) {
    return err(dbError('Failed to list cities', cause))
  }
}

/** Which offering covers which city — read whole, joined in memory. */
export async function listOfferingCities(
  db: Database,
): Promise<Result<OfferingCityRow[], DbError>> {
  try {
    return ok(await db.select().from(offeringCity))
  } catch (cause) {
    return err(dbError('Failed to list offering cities', cause))
  }
}

export async function getOfferingById(
  db: Database,
  id: string,
): Promise<Result<OfferingRow | null, DbError>> {
  try {
    const [row] = await db.select().from(offering).where(eq(offering.id, id)).limit(1)
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to load offering', cause))
  }
}

export async function getOfferingBySlug(
  db: Database,
  slug: string,
): Promise<Result<OfferingRow | null, DbError>> {
  try {
    const [row] = await db.select().from(offering).where(eq(offering.slug, slug)).limit(1)
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to load offering by slug', cause))
  }
}

export async function getCategoryById(
  db: Database,
  id: string,
): Promise<Result<CategoryRow | null, DbError>> {
  try {
    const [row] = await db
      .select()
      .from(offeringCategory)
      .where(eq(offeringCategory.id, id))
      .limit(1)
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to load offering category', cause))
  }
}

export async function getCityById(
  db: Database,
  id: string,
): Promise<Result<CityRow | null, DbError>> {
  try {
    const [row] = await db.select().from(city).where(eq(city.id, id)).limit(1)
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to load city', cause))
  }
}

// ── offerings ──────────────────────────────────────────────────────────────

/**
 * What a delete found in the way. `deleted: false` is a normal answer, not a
 * failure — the caller turns it into a sentence the admin can act on.
 */
export type DeleteOutcome = { deleted: true } | { deleted: false; blockedBy: number }

export async function createOffering(
  db: Database,
  values: typeof offering.$inferInsert,
): Promise<Result<OfferingRow, DbError>> {
  try {
    const [row] = await db.insert(offering).values(values).returning()
    if (!row) return err(dbError('Insert returned no offering row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to create offering', cause))
  }
}

export async function updateOffering(
  db: Database,
  id: string,
  patch: Partial<typeof offering.$inferInsert>,
): Promise<Result<OfferingRow | null, DbError>> {
  try {
    const [row] = await db
      .update(offering)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(offering.id, id))
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to update offering', cause))
  }
}

/**
 * Erase an offering for good — the escape hatch for "added by mistake". Hiding
 * it (`isActive: false`) remains the everyday move and is what the panel offers
 * first; this is the only operation in the catalogue that loses data.
 *
 * Refused while any booking points at it. `booking.offering_id` is `ON DELETE
 * RESTRICT`, so Postgres would refuse anyway — counting first is what turns an
 * opaque FK violation into "3 rezerwacje". The count and the delete share ONE
 * transaction so a booking made in between cannot slip through the gap; the FK
 * is still the backstop if it does.
 *
 * Order lines and leads are `ON DELETE SET NULL` and deliberately do NOT block:
 * an order line already carries its own label and price snapshot, so history
 * survives the catalogue row disappearing.
 */
/** What a catalogue row is holding, for the sentence above „Usuń pozycję". */
export interface OfferingUse {
  readonly variants: number
  /** Reservations — the one thing that REFUSES the delete outright. */
  readonly bookings: number
  /** Sold lines. They survive as their own snapshot; the link is what is lost. */
  readonly orderItems: number
  readonly cartItems: number
  /** Declared days that would go with the levels, and how many people wrote them. */
  readonly capacityDays: number
  /** Level grants, and the people who would lose them. */
  readonly skills: number
  readonly people: number
}

/**
 * Everything that would go, or change, if this offering were erased.
 *
 * The delete is one transaction of CASCADEs, and until this read existed the
 * panel asked „Na pewno?" without saying what „to" was: three levels, the days
 * two people had declared against them and seven grants that decide who may be
 * given the work. The owner deleting a row he believes is unused is entitled to
 * see that before he presses it, not afterwards.
 *
 * Counted per OFFERING and per its variants, because the cascades run down both
 * edges: `user_skill` and `capacity_day` hang off the level, carts and lines off
 * either.
 */
export async function readOfferingUse(
  db: Database,
  id: string,
): Promise<Result<OfferingUse, DbError>> {
  try {
    const variants = await db
      .select({ id: offeringVariant.id })
      .from(offeringVariant)
      .where(eq(offeringVariant.offeringId, id))
    const variantIds = variants.map((row) => row.id)
    const anyVariant = variantIds.length > 0

    const [booked, sold, carted, days, skills] = await Promise.all([
      db.select({ n: count() }).from(booking).where(eq(booking.offeringId, id)),
      db.select({ n: count() }).from(orderItem).where(eq(orderItem.offeringId, id)),
      db.select({ n: count() }).from(cartItem).where(eq(cartItem.offeringId, id)),
      anyVariant
        ? db
            .select({ n: count(), userId: capacityDay.userId })
            .from(capacityDay)
            .where(inArray(capacityDay.variantId, variantIds))
            .groupBy(capacityDay.userId)
        : Promise.resolve([]),
      anyVariant
        ? db
            .select({ n: count(), userId: userSkill.userId })
            .from(userSkill)
            .where(inArray(userSkill.variantId, variantIds))
            .groupBy(userSkill.userId)
        : Promise.resolve([]),
    ])

    const sum = (rows: readonly { n: number }[]) => rows.reduce((total, row) => total + row.n, 0)
    return ok({
      variants: variantIds.length,
      bookings: booked[0]?.n ?? 0,
      orderItems: sold[0]?.n ?? 0,
      cartItems: carted[0]?.n ?? 0,
      capacityDays: sum(days),
      skills: sum(skills),
      // The PEOPLE, not the rows: „7 nadań" is a number, „u 3 osób" is the
      // thing somebody has to go and explain.
      people: new Set([...skills, ...days].map((row) => row.userId)).size,
    })
  } catch (cause) {
    return err(dbError('Failed to read offering use', cause))
  }
}

export async function deleteOffering(
  db: Database,
  id: string,
): Promise<Result<DeleteOutcome, DbError>> {
  try {
    return ok(
      await db.transaction(async (tx) => {
        const [booked] = await tx
          .select({ n: count() })
          .from(booking)
          .where(eq(booking.offeringId, id))
        const blockedBy = booked?.n ?? 0
        if (blockedBy > 0) return { deleted: false as const, blockedBy }

        await tx.delete(offering).where(eq(offering.id, id))
        return { deleted: true as const }
      }),
    )
  } catch (cause) {
    return err(dbError('Failed to delete offering', cause))
  }
}

// ── variants ───────────────────────────────────────────────────────────────

export async function createOfferingVariant(
  db: Database,
  values: typeof offeringVariant.$inferInsert,
): Promise<Result<VariantRow, DbError>> {
  try {
    const [row] = await db.insert(offeringVariant).values(values).returning()
    if (!row) return err(dbError('Insert returned no offering variant row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to create offering variant', cause))
  }
}

export async function updateOfferingVariant(
  db: Database,
  id: string,
  patch: Partial<typeof offeringVariant.$inferInsert>,
): Promise<Result<VariantRow | null, DbError>> {
  try {
    const [row] = await db
      .update(offeringVariant)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(offeringVariant.id, id))
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to update offering variant', cause))
  }
}

/**
 * Erase one variant, with its matrix cells (`offering_variant_feature`
 * cascades). Nothing refuses it: `order_item.offering_variant_id` is `ON DELETE
 * SET NULL` and the line already carries its own label and price, so a placed
 * order keeps saying which package was bought. `false` means no such row.
 */
export async function deleteOfferingVariant(
  db: Database,
  id: string,
): Promise<Result<boolean, DbError>> {
  try {
    const deleted = await db
      .delete(offeringVariant)
      .where(eq(offeringVariant.id, id))
      .returning({ id: offeringVariant.id })
    return ok(deleted.length > 0)
  } catch (cause) {
    return err(dbError('Failed to delete offering variant', cause))
  }
}

// ── comparison matrix (a feature row and the variants that grant it) ────────

/**
 * Which variants include a feature, and with what qualifier (`Zaawansowane`,
 * `do 120 min`; `null` renders as a plain tick).
 *
 * Only the granting variants are listed — a variant absent from this array does
 * not include the feature, which is exactly how `offering_variant_feature` reads
 * a missing row. Storing an explicit "no" row would give the same answer twice.
 */
export type FeatureGrants = { variantId: string; valueLabel: string | null }[]

type Tx = Parameters<Parameters<Database['transaction']>[0]>[0]

/** Replace a feature's whole row of cells. Delete-then-insert: see below. */
async function writeGrants(tx: Tx, featureId: string, grants: FeatureGrants): Promise<void> {
  await tx
    .delete(offeringVariantFeature)
    .where(eq(offeringVariantFeature.offeringFeatureId, featureId))
  if (grants.length === 0) return
  await tx.insert(offeringVariantFeature).values(
    grants.map((grant) => ({
      offeringFeatureId: featureId,
      offeringVariantId: grant.variantId,
      isIncluded: true,
      valueLabel: grant.valueLabel,
    })),
  )
}

/**
 * A comparison row and the cells across it, written together.
 *
 * The whole row travels as one because that is how it is edited and how it is
 * read: "Napisy" means nothing without knowing what each variant does with it,
 * and a save that landed the label but not the cells would advertise a feature
 * no variant grants. One transaction, so the two cannot part company.
 */
export async function createOfferingFeature(
  db: Database,
  values: typeof offeringFeature.$inferInsert,
  grants: FeatureGrants,
): Promise<Result<FeatureRow, DbError>> {
  try {
    return ok(
      await db.transaction(async (tx) => {
        const [row] = await tx.insert(offeringFeature).values(values).returning()
        if (!row) throw new Error('Insert returned no offering feature row')
        await writeGrants(tx, row.id, grants)
        return row
      }),
    )
  } catch (cause) {
    return err(dbError('Failed to create offering feature', cause))
  }
}

/** `grants: null` edits the label alone and leaves the cells untouched. */
export async function updateOfferingFeature(
  db: Database,
  id: string,
  patch: Partial<typeof offeringFeature.$inferInsert>,
  grants: FeatureGrants | null,
): Promise<Result<FeatureRow | null, DbError>> {
  try {
    return ok(
      await db.transaction(async (tx) => {
        const [row] = await tx
          .update(offeringFeature)
          .set({ ...patch, updatedAt: new Date() })
          .where(eq(offeringFeature.id, id))
          .returning()
        if (!row) return null
        if (grants) await writeGrants(tx, id, grants)
        return row
      }),
    )
  } catch (cause) {
    return err(dbError('Failed to update offering feature', cause))
  }
}

export async function deleteOfferingFeature(
  db: Database,
  id: string,
): Promise<Result<boolean, DbError>> {
  try {
    const deleted = await db
      .delete(offeringFeature)
      .where(eq(offeringFeature.id, id))
      .returning({ id: offeringFeature.id })
    return ok(deleted.length > 0)
  } catch (cause) {
    return err(dbError('Failed to delete offering feature', cause))
  }
}

// ── quote options (price-free, for enquiries) ───────────────────────────────

export async function createOfferingQuoteOption(
  db: Database,
  values: typeof offeringQuoteOption.$inferInsert,
): Promise<Result<QuoteOptionRow, DbError>> {
  try {
    const [row] = await db.insert(offeringQuoteOption).values(values).returning()
    if (!row) return err(dbError('Insert returned no quote option row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to create quote option', cause))
  }
}

export async function updateOfferingQuoteOption(
  db: Database,
  id: string,
  patch: Partial<typeof offeringQuoteOption.$inferInsert>,
): Promise<Result<QuoteOptionRow | null, DbError>> {
  try {
    const [row] = await db
      .update(offeringQuoteOption)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(offeringQuoteOption.id, id))
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to update quote option', cause))
  }
}

export async function deleteOfferingQuoteOption(
  db: Database,
  id: string,
): Promise<Result<boolean, DbError>> {
  try {
    const deleted = await db
      .delete(offeringQuoteOption)
      .where(eq(offeringQuoteOption.id, id))
      .returning({ id: offeringQuoteOption.id })
    return ok(deleted.length > 0)
  } catch (cause) {
    return err(dbError('Failed to delete quote option', cause))
  }
}

// ── categories ─────────────────────────────────────────────────────────────

export async function createOfferingCategory(
  db: Database,
  values: typeof offeringCategory.$inferInsert,
): Promise<Result<CategoryRow, DbError>> {
  try {
    const [row] = await db.insert(offeringCategory).values(values).returning()
    if (!row) return err(dbError('Insert returned no offering category row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to create offering category', cause))
  }
}

export async function updateOfferingCategory(
  db: Database,
  id: string,
  patch: Partial<typeof offeringCategory.$inferInsert>,
): Promise<Result<CategoryRow | null, DbError>> {
  try {
    const [row] = await db
      .update(offeringCategory)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(offeringCategory.id, id))
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to update offering category', cause))
  }
}

/**
 * Delete a category, refused while it still holds offerings or child groups —
 * both are `ON DELETE RESTRICT`. Counting ALL of them, not just the active ones:
 * a hidden offering would be orphaned just as hard by a cascade that never runs.
 */
export async function deleteOfferingCategory(
  db: Database,
  id: string,
): Promise<Result<DeleteOutcome, DbError>> {
  try {
    return ok(
      await db.transaction(async (tx) => {
        const [held] = await tx
          .select({ n: count() })
          .from(offering)
          .where(eq(offering.categoryId, id))
        const [groups] = await tx
          .select({ n: count() })
          .from(offeringCategory)
          .where(eq(offeringCategory.parentId, id))
        const blockedBy = (held?.n ?? 0) + (groups?.n ?? 0)
        if (blockedBy > 0) return { deleted: false as const, blockedBy }

        await tx.delete(offeringCategory).where(eq(offeringCategory.id, id))
        return { deleted: true as const }
      }),
    )
  } catch (cause) {
    return err(dbError('Failed to delete offering category', cause))
  }
}

// ── cities and coverage ────────────────────────────────────────────────────

export async function createCity(
  db: Database,
  values: typeof city.$inferInsert,
): Promise<Result<CityRow, DbError>> {
  try {
    const [row] = await db.insert(city).values(values).returning()
    if (!row) return err(dbError('Insert returned no city row'))
    return ok(row)
  } catch (cause) {
    return err(dbError('Failed to create city', cause))
  }
}

export async function updateCity(
  db: Database,
  id: string,
  patch: Partial<typeof city.$inferInsert>,
): Promise<Result<CityRow | null, DbError>> {
  try {
    const [row] = await db
      .update(city)
      .set({ ...patch, updatedAt: new Date() })
      .where(eq(city.id, id))
      .returning()
    return ok(row ?? null)
  } catch (cause) {
    return err(dbError('Failed to update city', cause))
  }
}

/**
 * Delete a city. `offering_city` cascades, so coverage lists simply lose the
 * entry — but a booking that happened THERE is `ON DELETE SET NULL` and would
 * silently forget where it took place, so bookings block.
 */
export async function deleteCity(
  db: Database,
  id: string,
): Promise<Result<DeleteOutcome, DbError>> {
  try {
    return ok(
      await db.transaction(async (tx) => {
        const [booked] = await tx.select({ n: count() }).from(booking).where(eq(booking.cityId, id))
        const blockedBy = booked?.n ?? 0
        if (blockedBy > 0) return { deleted: false as const, blockedBy }

        await tx.delete(city).where(eq(city.id, id))
        return { deleted: true as const }
      }),
    )
  } catch (cause) {
    return err(dbError('Failed to delete city', cause))
  }
}

/**
 * Replace an offering's whole coverage list in one transaction. Delete-then-
 * insert for the same reason the matrix cells use it: the list IS the answer,
 * and a partial write would leave a photographer travelling to a city nobody
 * ticked.
 */
export async function setOfferingCities(
  db: Database,
  offeringId: string,
  cityIds: string[],
): Promise<Result<true, DbError>> {
  try {
    await db.transaction(async (tx) => {
      await tx.delete(offeringCity).where(eq(offeringCity.offeringId, offeringId))
      if (cityIds.length === 0) return
      await tx.insert(offeringCity).values(cityIds.map((cityId) => ({ offeringId, cityId })))
    })
    return ok(true)
  } catch (cause) {
    return err(dbError('Failed to set offering cities', cause))
  }
}

// ── discount ladders ───────────────────────────────────────────────────────

// ───────────────────────────────────────────────────────────────────────────
// Published opening hours (spec §10 · I1)
// ───────────────────────────────────────────────────────────────────────────

/**
 * One opening the calendar publishes.
 *
 * `offering_availability` has been in the schema since the start with nothing
 * reading it. Spec §10 is what turns it on: "kalendarz prowadzony przez Admina
 * lub Koordynatora… klient rezerwuje tylko z wpisanych godzin". A studio with
 * no rows is a studio that is CLOSED, which is the only reading that makes the
 * table mean anything.
 */
export interface OpenWindowRow {
  readonly id: string
  readonly offeringId: string
  readonly startsAt: Date
  readonly endsAt: Date
  readonly isBookable: boolean
  readonly note: string | null
}

/** Every published opening from `from` onward, for the offerings named. */
export async function listOpenWindows(
  db: Database,
  offeringIds: readonly string[],
  from: Date,
): Promise<Result<OpenWindowRow[], DbError>> {
  if (offeringIds.length === 0) return ok([])
  try {
    const rows = await db
      .select()
      .from(offeringAvailability)
      .where(
        and(
          inArray(offeringAvailability.offeringId, [...offeringIds]),
          gte(offeringAvailability.endsAt, from),
        ),
      )
      .orderBy(asc(offeringAvailability.startsAt))
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to read opening hours', cause))
  }
}

/**
 * Publish one opening.
 *
 * `offeringIds` plural because spec §10 asks for "jeden kalendarz na oba
 * studia": the hours are typed once and written for every room they apply to.
 * Two rooms with independently maintained calendars is exactly the drift the
 * sentence is there to prevent.
 */
export async function openWindows(
  db: Database,
  input: {
    readonly offeringIds: readonly string[]
    readonly startsAt: Date
    readonly endsAt: Date
    readonly isBookable?: boolean
    readonly note?: string | null
  },
): Promise<Result<OpenWindowRow[], DbError>> {
  if (input.offeringIds.length === 0) return ok([])
  try {
    const rows = await db
      .insert(offeringAvailability)
      .values(
        input.offeringIds.map((offeringId) => ({
          offeringId,
          startsAt: input.startsAt,
          endsAt: input.endsAt,
          isBookable: input.isBookable ?? true,
          note: input.note ?? null,
        })),
      )
      .returning()
    return ok(rows)
  } catch (cause) {
    return err(dbError('Failed to publish opening hours', cause))
  }
}

/** Withdraw one opening. Bookings already made inside it are untouched. */
export async function closeWindow(db: Database, id: string): Promise<Result<boolean, DbError>> {
  try {
    const rows = await db
      .delete(offeringAvailability)
      .where(eq(offeringAvailability.id, id))
      .returning({ id: offeringAvailability.id })
    return ok(rows.length > 0)
  } catch (cause) {
    return err(dbError('Failed to withdraw an opening', cause))
  }
}
